// orchestrator_fullstack/frontend/src/app/admin/oidc/UsersActivePage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import DangerConfirmModal from "../../components/DangerConfirmModal";
import {
  listProvisionedUsers,
  addEmailRole,
  type AuthUserTenantRow,
  type Role,
  toTenantUuid,
} from "./api";
import { apiFetch } from "../../auth/api";

const PROMOTE_ROLES: Role[] = ["viewer", "operator", "admin", "platform-admin"];

function normalizeEmail(v: any): string {
  return String(v || "").trim().toLowerCase();
}

function looksLikeEmail(v: any): boolean {
  const s = normalizeEmail(v);
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

// /v1/admin/tenants now returns:
// { tenant_id: <uuid>, tenant_slug: <slug>, name, active, created_at, updated_at }
type TenantRow = {
  tenant_id?: string;      // UUID
  tenant_slug?: string;    // slug/id
  name?: string | null;
  active?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
};

function tenantUuid(t: TenantRow): string {
  return String(t.tenant_id || "").trim();
}

function tenantLabel(t: TenantRow): string {
  const slug = String(t.tenant_slug || "").trim();
  const name = String(t.name || "").trim();
  if (name && slug && name !== slug) return `${name} (${slug})`;
  return name || slug || tenantUuid(t);
}

export default function UsersActivePage() {
  const { state } = useAuth();

  // /v1/auth/me tenant is UUID
  const currentTenantUuid = toTenantUuid(state.tenant || "");
  const rolesStr = (state.roles || []).join(", ");
  const isPlatformAdmin = (state.roles || []).includes("platform-admin");

  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rows, setRows] = useState<AuthUserTenantRow[]>([]);

  // ---- NEW: Tenant selector (platform-admin) ----
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  // This is the tenant UUID the page queries (defaults to currentTenantUuid)
  const [targetTenantUuid, setTargetTenantUuid] = useState<string>(currentTenantUuid);

  // Support-mode headers (platform-admin only)
  const [supportMode, setSupportMode] = useState(false);
  const [supportReason, setSupportReason] = useState("");

  const crossTenant = useMemo(() => {
    const tgt = toTenantUuid(targetTenantUuid);
    return !!tgt && !!currentTenantUuid && tgt !== currentTenantUuid;
  }, [targetTenantUuid, currentTenantUuid]);

  const supportModeValid = useMemo(() => {
    if (!supportMode) return true;
    return String(supportReason || "").trim().length >= 5;
  }, [supportMode, supportReason]);

  const supportHeaders = useMemo(() => {
    if (!isPlatformAdmin || !supportMode) return {};
    const reason = String(supportReason || "").trim();
    return {
      "X-Support-Mode": "1",
      ...(reason ? { "X-Support-Reason": reason } : {}),
    };
  }, [isPlatformAdmin, supportMode, supportReason]);

  // Promote modal state (platform-admin only)
  const [promote, setPromote] = useState<null | { email: string; currentRoles: string[] }>(null);
  const [promoteRole, setPromoteRole] = useState<Role>("operator");

  const effectiveTenantUuid = useMemo(() => {
    // Non-platform-admins are always scoped to their own tenant.
    if (!isPlatformAdmin) return currentTenantUuid;
    const tgt = toTenantUuid(targetTenantUuid);
    return tgt || currentTenantUuid;
  }, [isPlatformAdmin, targetTenantUuid, currentTenantUuid]);

  async function loadTenantsIfNeeded() {
    if (!isPlatformAdmin) return;
    try {
      const ts = await apiFetch<TenantRow[]>(`/v1/admin/tenants`, { admin: true });
      const list = (ts || []).filter((t) => tenantUuid(t));
      setTenants(list);

      // Initialize target selection:
      // - Prefer currentTenantUuid if present
      // - Else root if present
      // - Else first
      const hasCurrent = currentTenantUuid && list.some((t) => tenantUuid(t) === currentTenantUuid);
      const root = list.find((t) => String(t.tenant_slug || "").trim() === "root");
      const rootUuid = root ? tenantUuid(root) : "";
      const first = tenantUuid(list[0] || "");

      setTargetTenantUuid((prev) => {
        const p = toTenantUuid(prev);
        if (p && list.some((t) => tenantUuid(t) === p)) return p;
        if (hasCurrent) return currentTenantUuid;
        if (rootUuid) return rootUuid;
        return first || currentTenantUuid;
      });
    } catch {
      // Do not block the page if tenant list fails; keep existing behavior.
    }
  }

  async function refresh() {
    const tenantUuid = effectiveTenantUuid;
    if (!tenantUuid) return;

    // If cross-tenant read is requested, enforce Support Mode in the UI.
    if (isPlatformAdmin && crossTenant) {
      if (!supportMode) {
        setErr("Support Mode must be enabled to read users for a different tenant.");
        setRows([]);
        return;
      }
      if (!supportModeValid) {
        setErr("Support Mode reason is required (min 5 chars) for cross-tenant reads.");
        setRows([]);
        return;
      }
    }

    setBusy(true);
    setErr(null);
    try {
      const data = await listProvisionedUsers(tenantUuid, q, 200, supportHeaders);
      setRows(data || []);
    } catch (e: any) {
      setErr(e?.message || String(e));
      setRows([]);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadTenantsIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatformAdmin]);

  useEffect(() => {
    // When auth tenant changes, keep target in sync unless user already picked something else.
    // For non-platform-admins, this is always enforced.
    if (!isPlatformAdmin) {
      setTargetTenantUuid(currentTenantUuid);
    } else {
      setTargetTenantUuid((prev) => prev || currentTenantUuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantUuid]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTenantUuid, supportMode]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const email = String((r as any).email || "").toLowerCase();
      const sub = String((r as any).sub || "").toLowerCase();
      return email.includes(s) || sub.includes(s);
    });
  }, [rows, q]);

  const tenantOptions = useMemo(() => {
    return (tenants || [])
      .map((t) => ({ id: tenantUuid(t), label: tenantLabel(t) }))
      .filter((x) => x.id);
  }, [tenants]);

  if (!currentTenantUuid) {
    return (
      <div className="p-6">
        <div className="text-xl font-bold">Active Users</div>
        <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Tenant is not resolved from <code>/v1/auth/me</code>. This page requires a valid tenant context.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1200px]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-2xl font-extrabold">Active Users</div>
          <div className="text-sm text-zinc-600 mt-1">
            Derived access from provisioning tables.
            <span className="ml-2">
              Current tenant: <span className="font-mono">{currentTenantUuid}</span>
            </span>
            <span className="ml-2">
              · Viewing: <span className="font-mono">{effectiveTenantUuid}</span>
            </span>
            <span className="ml-2">
              · You: <span className="font-mono">{state.user || "(unknown)"}</span>
            </span>
            <span className="ml-2">
              · Roles: <span className="font-mono">{rolesStr || "(none)"}</span>
            </span>
          </div>

          <div className="mt-2 text-xs text-zinc-500">
            This view shows who has authenticated and what roles were derived (auth_users + auth_user_tenants).
          </div>

          <div className="mt-3 text-xs">
            <Link to="/admin/oidc/users-roles" className="text-blue-700 hover:underline">
              ← Back to Users & Roles (Mappings)
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            className="h-10 w-[260px] rounded-xl border border-zinc-200 px-3 text-sm"
            placeholder="Search by email or sub…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button
            className="h-10 rounded-xl border border-zinc-200 px-4 text-sm font-semibold hover:bg-zinc-50 disabled:opacity-50"
            disabled={busy}
            onClick={() => refresh()}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* NEW: Tenant selector (platform-admin only) */}
      {isPlatformAdmin && (
        <div className="mt-4 rounded-2xl border border-zinc-200 bg-white p-4">
          <div className="font-bold">Tenant</div>
          <div className="text-xs text-zinc-600 mt-1">
            Choose which tenant’s Active Users to view. Cross-tenant requires Support Mode.
          </div>

          <div className="mt-3 flex items-center gap-2">
            <select
              className="h-10 rounded-xl border border-zinc-200 px-3 text-sm"
              value={toTenantUuid(targetTenantUuid)}
              onChange={(e) => {
                setErr(null);
                setTargetTenantUuid(toTenantUuid(e.target.value));
              }}
            >
              {tenantOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>

            <button
              className="h-10 rounded-xl border border-zinc-200 px-4 text-sm font-semibold hover:bg-zinc-50 disabled:opacity-50"
              disabled={busy}
              onClick={async () => {
                await loadTenantsIfNeeded();
              }}
              title="Reload tenant list"
            >
              Reload
            </button>

            {crossTenant && (
              <span className="text-xs rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-800">
                Cross-tenant
              </span>
            )}
          </div>
        </div>
      )}

      {/* Support mode (platform-admin only) */}
      {isPlatformAdmin && (
        <div className="mt-4 rounded-2xl border border-zinc-200 bg-white p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-bold">Support Mode</div>
              <div className="text-xs text-zinc-600">
                Enables cross-tenant reads when you provide a reason. (This page uses tenant_id query param.)
              </div>
              {crossTenant && (
                <div className="mt-2 text-xs text-amber-700">
                  You are viewing a different tenant; Support Mode + reason is required.
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                checked={supportMode}
                onChange={(e) => setSupportMode(e.target.checked)}
              />
              Enabled
            </label>
          </div>

          {supportMode && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-zinc-700">Reason</div>
              <input
                className="mt-1 h-10 w-full rounded-xl border border-zinc-200 px-3 text-sm"
                value={supportReason}
                onChange={(e) => setSupportReason(e.target.value)}
                placeholder="e.g. Customer support case #12345 (min 5 chars)"
              />
              {!supportModeValid && (
                <div className="mt-2 text-xs text-red-600">
                  Reason is required when Support Mode is enabled.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {err && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 whitespace-pre-wrap">
          {err}
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div className="font-bold">Recently authenticated users</div>
          <div className="text-xs text-zinc-500">{filtered.length} users</div>
        </div>

        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-zinc-500">
                <th className="py-2">email</th>
                <th className="py-2">sub</th>
                <th className="py-2">roles</th>
                <th className="py-2">last_seen</th>
                <th className="py-2">updated</th>
                {isPlatformAdmin && <th className="py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const email = normalizeEmail((r as any).email);
                const canPromote = isPlatformAdmin && looksLikeEmail(email);
                const currentRoles = (r as any).roles || [];
                return (
                  <tr
                    key={`${String((r as any).tenant_id || "")}-${String((r as any).sub || "")}`}
                    className="border-t border-zinc-100"
                  >
                    <td className="py-2 font-mono">{email || "(none)"}</td>
                    <td className="py-2 font-mono text-xs">{String((r as any).sub || "")}</td>
                    <td className="py-2 font-mono text-xs">
                      {(currentRoles || []).join(", ") || "(none)"}
                    </td>
                    <td className="py-2 font-mono text-xs">{String((r as any).last_seen_at || "")}</td>
                    <td className="py-2 font-mono text-xs">{String((r as any).membership_updated_at || "")}</td>

                    {isPlatformAdmin && (
                      <td className="py-2 text-right">
                        <button
                          className="rounded-lg border border-zinc-200 px-3 py-1 text-xs font-semibold hover:bg-zinc-50 disabled:opacity-50"
                          disabled={busy || !canPromote}
                          title={
                            !canPromote
                              ? "User has no email to promote via email-role mapping."
                              : "Promote by creating an email-role mapping for this tenant."
                          }
                          onClick={() => {
                            setErr(null);
                            setPromoteRole("operator");
                            setPromote({ email, currentRoles });
                          }}
                        >
                          Promote…
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}

              {filtered.length === 0 && (
                <tr>
                  <td colSpan={isPlatformAdmin ? 6 : 5} className="py-4 text-xs text-zinc-500">
                    No authenticated users found for this tenant yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3 text-xs text-zinc-500">
          Tip: a user appears here after they successfully call <code>/v1/auth/me</code> with a valid JWT.
        </div>
      </div>

      {/* Promote modal (platform-admin only) */}
      {isPlatformAdmin && (
        <div className={promote ? "" : "hidden"}>
          <DangerConfirmModal
            open={!!promote}
            title="Promote user"
            description={
              promote
                ? `This will create an email-role mapping for tenant ${effectiveTenantUuid}: ${promote.email} → ${promoteRole}. The user must re-login to pick up the new role.`
                : ""
            }
            confirmKeyword="PROMOTE"
            confirmLabel="Promote"
            requireReason={false}
            onCancel={() => setPromote(null)}
            onConfirm={async () => {
              if (!promote) return;

              // If promoting on a different tenant than your current tenant, backend requires support mode.
              if (isPlatformAdmin && crossTenant) {
                if (!supportMode) {
                  setErr("Support Mode must be enabled to promote a user in a different tenant.");
                  return;
                }
                if (!supportModeValid) {
                  setErr("Support Mode reason is required (min 5 chars).");
                  return;
                }
              }

              setBusy(true);
              setErr(null);
              try {
                await addEmailRole(effectiveTenantUuid, promote.email, promoteRole, supportHeaders);
                setPromote(null);
                await refresh();
              } catch (e: any) {
                setErr(e?.message || String(e));
              } finally {
                setBusy(false);
              }
            }}
          />
        </div>
      )}

      {/* Small inline role picker (kept outside modal because DangerConfirmModal API is keyword-only) */}
      {isPlatformAdmin && promote && (
        <div className="fixed inset-0 z-[9998] pointer-events-none">
          <div className="pointer-events-none" />
        </div>
      )}

      {/* Role select rendered as a small fixed panel above the modal, so you can choose role before typing PROMOTE */}
      {isPlatformAdmin && promote && (
        <div className="fixed inset-0 z-[10000] flex items-start justify-center p-4 pointer-events-none">
          <div className="pointer-events-auto mt-[92px] w-full max-w-lg rounded-2xl border border-zinc-200 bg-white p-3 shadow-lg">
            <div className="text-xs font-semibold text-zinc-700">Target role</div>
            <div className="mt-2 flex items-center gap-2">
              <select
                className="h-10 flex-1 rounded-xl border border-zinc-200 px-3 text-sm"
                value={promoteRole}
                onChange={(e) => setPromoteRole(e.target.value as Role)}
                disabled={busy}
              >
                {PROMOTE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <div className="text-xs text-zinc-500">
                Current:{" "}
                <span className="font-mono">
                  {(promote.currentRoles || []).join(", ") || "(none)"}
                </span>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-zinc-500">
              This writes to <code>/v1/admin/oidc/email-role</code> (OIDC mappings), not provisioning roles.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}